import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import {
  PlusIcon, PencilIcon, DocumentTextIcon,
  XMarkIcon, AcademicCapIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { saveAs } from 'file-saver';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .ex-root { font-family:'Sora',sans-serif; color:#1a1d2e; }

  .ex-page-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:1.75rem; flex-wrap:wrap; gap:.75rem; }
  .ex-page-title  { font-size:1.6rem; font-weight:700; letter-spacing:-.03em; color:#0f1123; }
  .ex-page-sub    { font-size:.78rem; color:#9094a8; margin-top:2px; }

  .btn { padding:8px 18px; border-radius:10px; font-size:.8rem; font-weight:600; font-family:'Sora',sans-serif; cursor:pointer; border:none; display:inline-flex; align-items:center; gap:7px; transition:transform .15s,box-shadow .15s; }
  .btn:active { transform:scale(.97); }
  .btn svg { width:15px; height:15px; stroke-width:2; flex-shrink:0; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { box-shadow:0 4px 14px rgba(59,110,248,.35); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { box-shadow:0 4px 14px rgba(16,201,142,.35); }
  .btn-ghost { background:#f0f2f7; color:#6b7194; border:1px solid #e0e4f0; }
  .btn-ghost:hover { background:#e8ebf4; }

  .ex-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.25rem; }
  .ex-panel-header { display:flex; align-items:center; justify-content:space-between; padding:16px 20px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .ex-panel-header-left { display:flex; align-items:center; gap:10px; }
  .ex-panel-icon { width:34px; height:34px; border-radius:10px; display:flex; align-items:center; justify-content:center; background:#eef2ff; color:#3b6ef8; flex-shrink:0; }
  .ex-panel-icon svg { width:17px; height:17px; stroke-width:1.8; }
  .ex-panel-title { font-size:.88rem; font-weight:600; color:#1a1d2e; }
  .ex-panel-sub   { font-size:.72rem; color:#9094a8; margin-top:1px; }

  /* Desktop table */
  .ex-table { width:100%; border-collapse:collapse; font-size:.8rem; }
  .ex-table thead tr { background:#f8f9fc; border-bottom:1px solid #eaecf4; }
  .ex-table th { padding:11px 16px; text-align:center !important; font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .ex-table th:last-child { text-align:right; }
  .ex-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .ex-table tbody tr:last-child { border-bottom:none; }
  .ex-table tbody tr:hover { background:#fafbff; }
  .ex-table td { padding:13px 16px; color:#3a3f58; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .ex-table td:last-child { text-align:right; }

  .exam-name-cell { display:flex; align-items:center; gap:10px; }
  .exam-avatar { width:32px; height:32px; border-radius:9px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:.65rem; font-weight:700; color:white; flex-shrink:0; }
  .exam-name-main { font-weight:600; color:#1a1d2e; font-size:.83rem; }

  .tag-wrap { display:flex; flex-wrap:wrap; gap:4px; max-width:200px; }
  .tag { display:inline-block; padding:2px 8px; border-radius:99px; font-size:.68rem; font-weight:600; background:#ecfdf5; color:#10c98e; white-space:nowrap; }
  .tag.purple { background:#f3f0ff; color:#8b5cf6; }

  .mono { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }

  .publish-toggle { position:relative; display:inline-flex; align-items:center; cursor:pointer; gap:7px; font-size:.75rem; font-weight:600; }
  .publish-toggle input { display:none; }
  .toggle-track { width:36px; height:20px; background:#e0e4f0; border-radius:99px; position:relative; transition:background .2s; flex-shrink:0; }
  .toggle-thumb { position:absolute; top:3px; left:3px; width:14px; height:14px; background:white; border-radius:50%; transition:transform .2s; box-shadow:0 1px 3px rgba(0,0,0,.15); }
  .publish-toggle input:checked + .toggle-track { background:#10c98e; }
  .publish-toggle input:checked + .toggle-track .toggle-thumb { transform:translateX(16px); }
  .publish-label-on  { color:#10c98e; }
  .publish-label-off { color:#b0b5cc; }

  .action-btn { width:30px; height:30px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:inline-flex; align-items:center; justify-content:center; cursor:pointer; transition:background .13s,border-color .13s; margin-left:6px; }
  .action-btn svg { width:14px; height:14px; stroke-width:1.8; }
  .action-btn.marks  { color:#10c98e; }
  .action-btn.marks:hover  { background:#ecfdf5; border-color:#10c98e; }
  .action-btn.edit   { color:#3b6ef8; }
  .action-btn.edit:hover   { background:#eef2ff; border-color:#3b6ef8; }
  .action-btn.delete { color:#ef4444; }
  .action-btn.delete:hover { background:#fff1f1; border-color:#fecaca; }

  /* Mobile exam cards */
  .ex-cards { display:none; padding:12px; }
  .ex-card { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:14px; margin-bottom:10px; }
  .ex-card-top { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:10px; gap:8px; }
  .ex-card-row { display:flex; justify-content:space-between; align-items:center; font-size:.76rem; padding:5px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .ex-card-row:last-of-type { border-bottom:none; }
  .ex-card-label { color:#9094a8; font-weight:600; white-space:nowrap; }
  .ex-card-actions { display:flex; gap:6px; margin-top:10px; }
  .ex-card-actions .action-btn { margin-left:0; }

  .ex-empty { text-align:center; padding:56px 20px; color:#b0b5cc; font-size:.82rem; }
  .spinner { width:16px; height:16px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  .modal-overlay { position:fixed; inset:0; background:rgba(10,12,28,.55); backdrop-filter:blur(3px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; animation:overlayIn .2s ease; }
  @keyframes overlayIn { from{opacity:0} to{opacity:1} }
  .modal-box { background:white; border-radius:20px; width:100%; border:1px solid #e8ebf4; box-shadow:0 24px 64px rgba(0,0,0,.18); animation:modalIn .25s ease; overflow:hidden; display:flex; flex-direction:column; max-height:90vh; }
  @keyframes modalIn { from{opacity:0;transform:translateY(16px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
  .modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; flex-shrink:0; gap:8px; }
  .modal-title { font-size:.95rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .modal-sub   { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .modal-close { width:30px; height:30px; border-radius:8px; border:1px solid #e8ebf4; background:#f8f9fc; display:flex; align-items:center; justify-content:center; cursor:pointer; color:#9094a8; flex-shrink:0; }
  .modal-close:hover { background:#f0f2f7; color:#1a1d2e; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body { padding:22px; overflow-y:auto; flex:1; }
  .modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f2f8; flex-shrink:0; flex-wrap:wrap; }

  .form-grid { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .form-grid > *, .fg > * { min-width:0; }
  .form-field { display:flex; flex-direction:column; gap:5px; }
  .form-field.full { grid-column:1/-1; }
  .form-label { font-size:.7rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 12px; border:1px solid #e0e4f0; border-radius:10px; background:#f8f9fc; color:#1a1d2e; outline:none; transition:border-color .15s,background .15s,box-shadow .15s; width:100%; }
  .form-input:focus,.form-select:focus { border-color:#3b6ef8; background:white; box-shadow:0 0 0 3px rgba(59,110,248,.08); }
  .form-hint { font-size:.68rem; color:#b0b5cc; }

  .marks-table-wrap { overflow-x:auto; -webkit-overflow-scrolling:touch; }
  .marks-table { width:100%; border-collapse:collapse; font-size:.8rem; min-width:500px; }
  .marks-table thead tr { background:#f8f9fc; border-bottom:1px solid #eaecf4; }
  .marks-table th { padding:10px 14px; text-align:center !important; font-size:.67rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .marks-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .marks-table tbody tr:last-child { border-bottom:none; }
  .marks-table tbody tr:hover { background:#fafbff; }
  .marks-table td { padding:10px 14px; vertical-align:middle; text-align:center !important; }
  .student-cell { display:flex; align-items:center; gap:8px; white-space:nowrap; font-weight:500; color:#1a1d2e; font-size:.8rem; }
  .student-mini-avatar { width:26px; height:26px; border-radius:7px; background:linear-gradient(135deg,#3b6ef8,#8b5cf6); display:flex; align-items:center; justify-content:center; font-size:.6rem; font-weight:700; color:white; flex-shrink:0; }
  .marks-input { font-family:'JetBrains Mono',monospace; font-size:.8rem; width:72px; padding:6px 10px; border:1px solid #e0e4f0; border-radius:8px; background:#f8f9fc; color:#1a1d2e; outline:none; text-align:center; transition:border-color .15s,background .15s; }
  .marks-input:focus { border-color:#3b6ef8; background:white; box-shadow:0 0 0 3px rgba(59,110,248,.08); }
  .max-marks-badge { font-size:.65rem; color:#b0b5cc; font-family:'JetBrains Mono',monospace; margin-top:2px; text-align:center; }

  .ex-fade { animation:exFadeUp .4s ease both; }
  @keyframes exFadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }

  /* ── Mobile ── */
  @media (max-width:768px) {
    .ex-page-title { font-size:1.2rem; }
    .ex-page-header { margin-bottom:1rem; }

    /* Hide table, show cards */
    .ex-table-wrap { display:none !important; }
    .ex-cards { display:block; }

    /* Marks modal scrolls horizontally — table already has overflow-x:auto */
    .modal-body { padding:14px; }
    .modal-footer { justify-content:stretch; }
    .modal-footer .btn { flex:1; justify-content:center; }
    .form-grid { grid-template-columns:1fr; }
    .form-field.full { grid-column:1; }
  }
`;

const getInitials = (name = '') =>
  name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'EX';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US',{day:'2-digit',month:'short',year:'numeric'}) : '—';

const Exams = () => {
  const [exams,          setExams]          = useState([]);
  const [classes,        setClasses]        = useState([]);
  const [students,       setStudents]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [showModal,      setShowModal]      = useState(false);
  const [editingExam,    setEditingExam]    = useState(null);
  const [showMarksModal, setShowMarksModal] = useState(false);
  const [selectedExam,   setSelectedExam]   = useState(null);
  const [marksData,      setMarksData]      = useState([]);
  const [formData,       setFormData]       = useState({ name:'', class:'', subjects:'', startDate:'', endDate:'', maxMarks:100 });

  useEffect(() => { fetchExams(); fetchClasses(); }, []);

  const fetchExams = async () => {
    try { setLoading(true); const { data } = await api.get('/admin/exams'); setExams(data); }
    catch { toast.error('Failed to fetch exams'); }
    finally { setLoading(false); }
  };

  const fetchClasses = async () => {
    try { const { data } = await api.get('/admin/classes'); setClasses(data); } catch {}
  };

  const fetchStudentsByClass = async (classId) => {
    try { const { data } = await api.get(`/admin/students?class=${classId}&limit=100`); return data.students; }
    catch { toast.error('Failed to fetch students'); return []; }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData, subjects: formData.subjects.split(',').map(s => s.trim()) };
      if (editingExam) { await api.put(`/admin/exams/${editingExam._id}`, dataToSend); toast.success('Exam updated'); }
      else             { await api.post('/admin/exams', dataToSend); toast.success('Exam created'); }
      setShowModal(false); fetchExams();
    } catch (error) { toast.error(error.response?.data?.message || 'Operation failed'); }
  };

  const handlePublish = async (examId, published) => {
    try { await api.put(`/admin/exams/${examId}/publish`, { published }); toast.success(published ? 'Results published' : 'Results unpublished'); fetchExams(); }
    catch { toast.error('Failed to update publish status'); }
  };

  const openMarksModal = async (exam) => {
    setSelectedExam(exam);
    const studs = await fetchStudentsByClass(exam.class._id);
    setStudents(studs);
    setMarksData(studs.map(s => ({ studentId: s._id, studentName: s.user.name, marks: exam.subjects.map(subject => ({ subject, marksObtained:'' })) })));
    setShowMarksModal(true);
  };

  const updateMarks = (sIdx, mIdx, val) => {
    const updated = [...marksData]; updated[sIdx].marks[mIdx].marksObtained = val; setMarksData(updated);
  };

  const handleSaveMarks = async () => {
    try { await api.post(`/admin/exams/${selectedExam._id}/marks`, { marksData }); toast.success('Marks saved'); setShowMarksModal(false); }
    catch { toast.error('Failed to save marks'); }
  };

  const openEditModal = (exam) => {
    setEditingExam(exam);
    setFormData({ name: exam.name, class: exam.class._id, subjects: exam.subjects.join(', '), startDate: exam.startDate?.split('T')[0]||'', endDate: exam.endDate?.split('T')[0]||'', maxMarks: exam.maxMarks });
    setShowModal(true);
  };

  const openAddModal = () => {
    setEditingExam(null);
    setFormData({ name:'', class:'', subjects:'', startDate:'', endDate:'', maxMarks:100 });
    setShowModal(true);
  };

  const renderCards = () => (
    <div className="ex-cards">
      {exams.map(exam => (
        <div key={exam._id} className="ex-card">
          <div className="ex-card-top">
            <div className="exam-name-cell">
              <div className="exam-avatar">{getInitials(exam.name)}</div>
              <div>
                <div className="exam-name-main">{exam.name}</div>
                <div style={{fontSize:'.68rem',color:'#9094a8'}}>Max: {exam.maxMarks} marks</div>
              </div>
            </div>
          </div>
          <div className="ex-card-row">
            <span className="ex-card-label">Class</span>
            <span className="tag purple">{exam.class?.name}</span>
          </div>
          <div className="ex-card-row">
            <span className="ex-card-label">Subjects</span>
            <div className="tag-wrap" style={{justifyContent:'flex-end'}}>
              {exam.subjects?.slice(0,3).map(s => <span key={s} className="tag">{s}</span>)}
              {exam.subjects?.length > 3 && <span className="tag" style={{background:'#f0f2f7',color:'#9094a8'}}>+{exam.subjects.length-3}</span>}
            </div>
          </div>
          <div className="ex-card-row">
            <span className="ex-card-label">Dates</span>
            <span className="mono">{formatDate(exam.startDate)} → {formatDate(exam.endDate)}</span>
          </div>
          <div className="ex-card-row">
            <span className="ex-card-label">Status</span>
            <label className="publish-toggle">
              <input type="checkbox" checked={exam.published} onChange={e => handlePublish(exam._id, e.target.checked)} />
              <div className="toggle-track"><div className="toggle-thumb" /></div>
              <span className={exam.published ? 'publish-label-on' : 'publish-label-off'}>{exam.published ? 'Published' : 'Draft'}</span>
            </label>
          </div>
          <div className="ex-card-actions">
            <button className="action-btn marks" onClick={() => openMarksModal(exam)} title="Enter Marks"><DocumentTextIcon /></button>
            <button className="action-btn edit"   onClick={() => openEditModal(exam)}  title="Edit"><PencilIcon /></button>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      <style>{style}</style>
      <div className="ex-root ex-fade">

        <div className="ex-page-header">
          <div>
            <h1 className="ex-page-title">Exams & Results</h1>
            <p className="ex-page-sub">Schedule exams, enter marks and publish student results.</p>
          </div>
          <button className="btn btn-blue" onClick={openAddModal}><PlusIcon />Create Exam</button>
        </div>

        <div className="ex-panel">
          <div className="ex-panel-header">
            <div className="ex-panel-header-left">
              <div className="ex-panel-icon"><AcademicCapIcon /></div>
              <div>
                <div className="ex-panel-title">All Exams</div>
                <div className="ex-panel-sub">{exams.length} exam{exams.length !== 1 ? 's' : ''} scheduled</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'48px 0',color:'#9094a8',fontSize:'.82rem'}}>
              <div className="spinner"/>Loading exams…
            </div>
          ) : exams.length === 0 ? (
            <div className="ex-empty">No exams found. Create your first exam to get started.</div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="ex-table-wrap" style={{overflowX:'auto'}}>
                <table className="ex-table">
                  <thead>
                    <tr><th>Exam</th><th>Class</th><th>Subjects</th><th>Start</th><th>End</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {exams.map(exam => (
                      <tr key={exam._id}>
                        <td><div className="exam-name-cell"><div className="exam-avatar">{getInitials(exam.name)}</div><div><div className="exam-name-main">{exam.name}</div><div style={{fontSize:'.68rem',color:'#9094a8'}}>Max: {exam.maxMarks}</div></div></div></td>
                        <td><span className="tag purple">{exam.class?.name}</span></td>
                        <td><div className="tag-wrap">{exam.subjects?.slice(0,3).map(s=><span key={s} className="tag">{s}</span>)}{exam.subjects?.length>3&&<span className="tag" style={{background:'#f0f2f7',color:'#9094a8'}}>+{exam.subjects.length-3}</span>}</div></td>
                        <td className="mono">{formatDate(exam.startDate)}</td>
                        <td className="mono">{formatDate(exam.endDate)}</td>
                        <td><label className="publish-toggle"><input type="checkbox" checked={exam.published} onChange={e=>handlePublish(exam._id,e.target.checked)}/><div className="toggle-track"><div className="toggle-thumb"/></div><span className={exam.published?'publish-label-on':'publish-label-off'}>{exam.published?'Published':'Draft'}</span></label></td>
                        <td><button className="action-btn marks" onClick={()=>openMarksModal(exam)} title="Marks"><DocumentTextIcon /></button><button className="action-btn edit" onClick={()=>openEditModal(exam)} title="Edit"><PencilIcon /></button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Mobile cards */}
              {renderCards()}
            </>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && createPortal(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div className="modal-box" style={{maxWidth:500}}>
            <div className="modal-header">
              <div><div className="modal-title">{editingExam?'Edit Exam':'Create New Exam'}</div><div className="modal-sub">Fill in the details below.</div></div>
              <button className="modal-close" onClick={()=>setShowModal(false)}><XMarkIcon /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-field full">
                    <label className="form-label">Exam Name</label>
                    <input type="text" className="form-input" placeholder="e.g. Mid-Term Examination 2025" value={formData.name} onChange={e=>setFormData({...formData,name:e.target.value})} required />
                  </div>
                  <div className="form-field">
                    <label className="form-label">Class</label>
                    <select className="form-select" value={formData.class} onChange={e=>setFormData({...formData,class:e.target.value})} required>
                      <option value="">— Select Class —</option>
                      {classes.map(c=><option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Max Marks / Subject</label>
                    <input type="number" className="form-input" placeholder="100" value={formData.maxMarks} onChange={e=>setFormData({...formData,maxMarks:e.target.value})} />
                  </div>
                  <div className="form-field full">
                    <label className="form-label">Subjects</label>
                    <input type="text" className="form-input" placeholder="Math, Science, English, Urdu" value={formData.subjects} onChange={e=>setFormData({...formData,subjects:e.target.value})} required />
                    <span className="form-hint">Comma-separated values</span>
                  </div>
                  <div className="form-field">
                    <label className="form-label">Start Date</label>
                    <input type="date" className="form-input" value={formData.startDate} onChange={e=>setFormData({...formData,startDate:e.target.value})} />
                  </div>
                  <div className="form-field">
                    <label className="form-label">End Date</label>
                    <input type="date" className="form-input" value={formData.endDate} onChange={e=>setFormData({...formData,endDate:e.target.value})} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={()=>setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-blue">{editingExam?'Update Exam':'Create Exam'}</button>
              </div>
            </form>
          </div>
        </div>
      
      , document.body)}

      {/* Marks Modal */}
      {showMarksModal && selectedExam && createPortal(
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowMarksModal(false)}>
          <div className="modal-box" style={{maxWidth:860}}>
            <div className="modal-header">
              <div>
                <div className="modal-title">Enter Marks — {selectedExam.name}</div>
                <div className="modal-sub">{selectedExam.class?.name} · Max {selectedExam.maxMarks} marks · {marksData.length} students</div>
              </div>
              <button className="modal-close" onClick={()=>setShowMarksModal(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              {marksData.length === 0 ? (
                <div className="ex-empty">No students found in this class.</div>
              ) : (
                <div className="marks-table-wrap">
                  <table className="marks-table">
                    <thead>
                      <tr>
                        <th>Student</th>
                        {selectedExam.subjects.map((s,i) => <th key={i}>{s}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {marksData.map((student, sIdx) => (
                        <tr key={student.studentId}>
                          <td><div className="student-cell"><div className="student-mini-avatar">{getInitials(student.studentName)}</div>{student.studentName}</div></td>
                          {student.marks.map((mark, mIdx) => (
                            <td key={mIdx}>
                              <input type="number" className="marks-input" placeholder="—" value={mark.marksObtained} onChange={e=>updateMarks(sIdx,mIdx,e.target.value)} max={selectedExam.maxMarks} min={0} />
                              <div className="max-marks-badge">/ {selectedExam.maxMarks}</div>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={()=>setShowMarksModal(false)}>Cancel</button>
              <button className="btn btn-green" onClick={handleSaveMarks}><DocumentTextIcon />Save Marks</button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </>
  );
};

export default Exams;